/**
 * Guided builder types for the frontend.
 * These mirror the types in @fias/builder-types/src/claude-integration.ts.
 * If those change, update these to match.
 */

export type GuidedPhase =
  | 'ideation'
  | 'clarification'
  | 'planning'
  | 'assumptions'
  | 'execution'
  | 'complete';

export type BuilderMode = 'guided' | 'chat';

export interface ClarifyingQA {
  question: string;
  answer: string | null;
}

export interface PlanAssumption {
  id: string;
  category: 'scope' | 'behavior' | 'content' | 'integration' | 'design';
  assumption: string;
  userFeedback: string | null;
  status: 'pending' | 'accepted' | 'revised';
}

export interface GuidedFlow {
  phase: GuidedPhase;
  ideaSummary: string;
  clarifyingQA: ClarifyingQA[];
  assumptions: PlanAssumption[];
  requireStepApproval: boolean;
  lastPhaseTransition: string;
}

export type GraphNodeType = 'user-input' | 'ai-process' | 'code' | 'entity-ref' | 'arche-ref';

export interface GraphNodePort {
  id: string;
  label: string;
  dataType: string;
}

export interface GraphNode {
  id: string;
  type: GraphNodeType;
  title: string;
  description: string;
  inputs: GraphNodePort[];
  outputs: GraphNodePort[];
  entityId?: string;
  archeId?: string;
  components: string[];
  actions: string[];
  aiConfigIds: string[];
  status: 'pending' | 'building' | 'verified';
  verificationAttempts?: number;
  editCount?: number;
  position: { x: number; y: number };
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  sourcePort?: string;
  targetPort?: string;
  label?: string;
}

export interface ProcessGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface BuildStep {
  id: string;
  title: string;
  components: string[];
  actions: string[];
  aiConfigIds: string[];
  status: 'pending' | 'building' | 'verified';
  verificationAttempts?: number;
  editCount?: number;
}

export interface CompletionCriterion {
  id: string;
  label: string;
  type: 'ai_config' | 'ai_action' | 'action_exists' | 'state_key';
  params: Record<string, string>;
  verified: boolean;
}

export interface BuildPlan {
  name: string;
  description: string;
  graph?: ProcessGraph;
  steps: BuildStep[];
  stateKeys: string[];
  currentNodeId?: string | null;
  currentStepIndex: number;
  approved: boolean;
  completionCriteria: CompletionCriterion[];
}

/** Wizard step definitions for the guided builder UI */
export const WIZARD_STEPS = [
  { index: 0, label: 'Describe' },
  { index: 1, label: 'Clarify' },
  { index: 2, label: 'Plan' },
  { index: 3, label: 'Review' },
  { index: 4, label: 'Build' },
] as const;

/** Map backend GuidedPhase to wizard step index (0-4) */
export function phaseToWizardStep(phase: GuidedPhase): number {
  switch (phase) {
    case 'ideation':
      return 0;
    case 'clarification':
      return 1;
    case 'planning':
      return 2;
    case 'assumptions':
      return 3;
    case 'execution':
    case 'complete':
      return 4;
  }
}
